import { Agency, agencyBasic, LocalDateType } from '@app/models';
import { baseOperationPoint, DictionaryEntity, OperationPoint } from '@app/pcs/models';
import { activityTypeBasic } from '@app/pcs/models/activity-type';
import { Calendar, calendarDetailed, CalendarEntities, calendarSchema } from '@app/pcs/models/calendar';
import { CatalogData, pathSectionCatalogDataDetailed } from '@dossier/models/catalog-data';
import { ParameterChangesSection } from '@dossier/models/parameter-changes-section';
import { processStrategyForChild } from '@dossier/models/schema-process-strategy';
import { TerritoryWithBlocks } from '@dossier/models/territory';
import { TrainParameters, trainParametersDetailed } from '@dossier/models/train-parameters';
import { UsedCatalogData, usedCatalogDataDetailed } from '@dossier/models/used-catalog-data';
import { Action } from '@ngrx/store';
import { safeProp } from '@util';
import { gql } from 'apollo-angular';
import { unbox } from 'ngrx-forms';
import { schema } from 'normalizr';
import * as R from 'ramda';

export const defaultTimeZoneOffset = {id: '0', description: 'UTC+1'};

export interface TimeZoneOffset {
  id: string;
  description?: string;
}

export interface UpdateTimeZonePayload {
  timeZone: TimeZoneOffset;
  blockIndex: number;
  psIndex: number;
  arrivalUpdate: boolean;
}

interface Identifiers {
  pathNumber?: string;
  trainNumber?: string;
}

export interface Time {
  time?: LocalDateType;
  offset?: number;
  manualOffset?: number;
  timeZone?: TimeZoneOffset;
}

export interface NegativeBorderTime {
  actualArrivalTime?: boolean;
  actualDepartureTime?: boolean;
}

export interface Times {
  arrival?: Time;
  departure?: Time;
  dwellTime?: number;
}

export interface BasePathSection {
  id?: number;
  earliestTimes?: Times;
  alternativeOrigin?: boolean;
  alternativeDestination?: boolean;
  papId?: string;
  identifiers?: Identifiers;
  border?: boolean;
  constructionPoint?: boolean;
  latestTimes?: Times;
  operationPoint?: OperationPoint;
  responsibleImAgency?: Agency;
  responsibleApplicantAgency?: Agency;
  times?: Times;
  publicTimes?: Times;
  details?: string;
  missingTrainParameters?: boolean;
  trainParameters?: TrainParameters;
  usedCatalogData?: UsedCatalogData;
  catalogData?: CatalogData;
  trainParameterChanges?: ParameterChangesSection[][];
  imParameterChanges?: ParameterChangesSection[];
  activityTypes?: DictionaryEntity[];
  locationTypes?: DictionaryEntity[];
}

export interface PathSection extends BasePathSection {
  calendar?: Calendar;
}

export interface PathSectionEntity extends BasePathSection {
  subPathId: number;
  calendar?: number;
}

export interface PathSectionsEntities {
  [key: number]: PathSectionEntity;
}

export interface NormalizedPathSections {
  entities: {
    pathElements: PathSectionsEntities;
    calendar?: CalendarEntities;
  };
  result: number[];
}

export interface PathSectionPermissions {
  showAlternativeOrigin?: boolean;
  showAlternativeDestination?: boolean;
  showMoveToNextTerritory?: boolean;
  showMoveToPreviousTerritory?: boolean;
  showReferencePoint?: boolean;
  showAddBefore?: boolean;
  showAddAfter?: boolean;
  showCataloguePathNumber?: boolean;
  showProtectedBorder?: boolean;
  canEditProtectedBorder?: boolean;
  canRemove?: boolean;
  canEditPap?: boolean;
  canRemovePap?: boolean;
}

export interface PathSectionAddRemoveInfo {
  psCumulativeIndex: number;
  psIndexInBlock: number;
  blockIndex: number;
  addedByAgency?: Agency;
  isBorder?: boolean;
  defaultLocationType?: DictionaryEntity;
  additionalActions?: Action[];
  territory?: TerritoryWithBlocks;
  belongsToPaP?: boolean;
  name?: string;
}

export enum PathSectionPropertyMap {
  Details = 'fromOpDetail',
  PathNumber = 'natTrasseNr',
  TrainNumber = 'natTrainNr',
  OperationPoint = 'OperationPointAndDetail',
  ActualArrivalTimeOffset = 'actualArrivalTimeOffset',
  ActualDepartureTimeOffset = 'actualDepartureTimeOffset',
  ArrivalTime = 'ArrivalTimes',
  DepartureTime = 'DepartureTimes',
  DwellTime = 'minDwellPeriodTime',
  TrainParameters = 'trainParameters',
  Calendar = 'calendarItem',
  SortOrder = 'sortOrder',
  ConstructionPoint = 'constructionPoint',
  LocationTypes = 'teLocationTypes',
  ActivityTypes = 'teActivityTypes',
  LocationParameters = 'LocationParameters'
}

export const timesDetailed = gql`
  fragment timesDetailed on Times {
    arrival {
      time
      offset
      manualOffset
      timeZone {
        id
        description
      }
    }
    departure {
      time
      offset
      manualOffset
      timeZone {
        id
        description
      }
    }
    dwellTime
  }
`;

export const identifiersDetailed = gql`
  fragment identifiersDetailed on Identifiers {
    pathNumber
    trainNumber
  }
`;

export const pathSectionWithBaseTrainParameters = gql`
  fragment pathSectionWithBaseTrainParameters on PathSection {
    id
    persistedId
    constructionPoint
    identifiers {
      ...identifiersDetailed
    }
    alternativeOrigin
    alternativeDestination
    operationPoint {
      ...baseOperationPoint
    }
    details
    missingTrainParameters
    times {
      ...timesDetailed
    }
    latestTimes {
      ...timesDetailed
    }
    earliestTimes {
      ...timesDetailed
    }
    publicTimes {
      ...timesDetailed
    }
    usedCatalogData {
      ...usedCatalogDataDetailed
    }
    catalogData {
      ...pathSectionCatalogDataDetailed
    }
    activityTypes {
      ...activityTypeBasic
    }
    locationTypes {
      id
      description
    }
  }
  ${timesDetailed}
  ${identifiersDetailed}
  ${baseOperationPoint}
  ${usedCatalogDataDetailed}
  ${pathSectionCatalogDataDetailed}
  ${activityTypeBasic}
`;

export const pathSectionWithTrainParameters = gql`
  fragment pathSectionWithTrainParameters on PathSection {
    id
    persistedId
    constructionPoint
    identifiers {
      ...identifiersDetailed
    }
    alternativeOrigin
    alternativeDestination
    operationPoint {
      ...baseOperationPoint
    }
    details
    missingTrainParameters
    times {
      ...timesDetailed
    }
    latestTimes {
      ...timesDetailed
    }
    earliestTimes {
      ...timesDetailed
    }
    publicTimes {
      ...timesDetailed
    }
    trainParameters {
      ...trainParametersDetailed
    }
    usedCatalogData {
      ...usedCatalogDataDetailed
    }
    catalogData {
      ...pathSectionCatalogDataDetailed
    }
    activityTypes {
      ...activityTypeBasic
    }
    locationTypes {
      id
      description
    }
  }
  ${timesDetailed}
  ${identifiersDetailed}
  ${baseOperationPoint}
  ${trainParametersDetailed}
  ${usedCatalogDataDetailed}
  ${pathSectionCatalogDataDetailed}
  ${activityTypeBasic}
`;

export const pathSectionWithCalendar = gql`
  fragment pathSectionWithCalendar on PathSection {
    ...pathSectionWithBaseTrainParameters
    calendar {
      ...calendarDetailed
    }
  }
  ${pathSectionWithBaseTrainParameters}
  ${calendarDetailed}
`;

export const pathSectionDetailed = gql`
  fragment pathSectionDetailed on PathSection {
    ...pathSectionWithTrainParameters
    calendar {
      ...calendarDetailed
    }
  }
  ${pathSectionWithTrainParameters}
  ${calendarDetailed}
`;

export const variantSectionWithoutTrainParameters = gql`
  fragment variantSectionWithoutTrainParameters on VariantSection {
    id
    persistedId
    constructionPoint
    border
    details
    alternativeOrigin
    alternativeDestination
    identifiers {
      ...identifiersDetailed
    }
    operationPoint {
      ...baseOperationPoint
    }
    details
    times {
      ...timesDetailed
    }
    earliestTimes {
      ...timesDetailed
    }
    latestTimes {
      ...timesDetailed
    }
    publicTimes {
      ...timesDetailed
    }
    responsibleApplicantAgency {
      ...agencyBasic
    }
    responsibleImAgency {
      ...agencyBasic
    }
    usedCatalogData {
      ...usedCatalogDataDetailed
    }
    catalogData {
      ...pathSectionCatalogDataDetailed
    }
    activityTypes {
      ...activityTypeBasic
    }
    locationTypes {
      id
      description
    }
  }
  ${timesDetailed}
  ${identifiersDetailed}
  ${baseOperationPoint}
  ${usedCatalogDataDetailed}
  ${agencyBasic}
  ${pathSectionCatalogDataDetailed}
  ${activityTypeBasic}
`;

export const variantSectionWithTrainParameters = gql`
  fragment variantSectionWithTrainParameters on VariantSection {
    ...variantSectionWithoutTrainParameters
    trainParameters {
      ...trainParametersDetailed
    }
  }
  ${variantSectionWithoutTrainParameters}
  ${trainParametersDetailed}
`;

export const variantSectionWithCalendars = gql`
  fragment variantSectionWithCalendars on VariantSection {
    ...variantSectionWithoutTrainParameters
    calendar {
      ...calendarDetailed
    }
  }
  ${variantSectionWithoutTrainParameters}
  ${calendarDetailed}
`;

export const variantSectionDetailed = gql`
  fragment variantSectionDetailed on VariantSection {
    ...variantSectionWithoutTrainParameters
    trainParameters {
      ...trainParametersDetailed
    }
    calendar {
      ...calendarDetailed
    }
  }
  ${variantSectionWithoutTrainParameters}
  ${trainParametersDetailed}
  ${calendarDetailed}
`;

export const pathElementsSchema = new schema.Entity('pathElements', {
  calendar: calendarSchema
}, {
  idAttribute: 'id',
  processStrategy: processStrategyForChild('subPath')
});

type GetUsedCatalogData = (p: PathSection) => UsedCatalogData;
export const getUsedCatalogData = <GetUsedCatalogData>safeProp('usedCatalogData');

type GetTrainParameters = (ps: PathSection) => TrainParameters;
export const getTrainParameters: GetTrainParameters = R.prop('trainParameters');

type GetTime = (p: PathSection) => number[];
export const getArrivalTime = <GetTime>R.compose(unbox, R.prop('time'), R.prop('arrival'), R.prop('times'));
export const getDepartureTime = <GetTime>R.compose(unbox, R.prop('time'), R.prop('departure'), R.prop('times'));

type GetCatalogData = (p: PathSection) => CatalogData;
export const getCatalogData = <GetCatalogData>safeProp('catalogData');

type GetCatalogResponsibleImAgency = (pathSection: PathSection) => Agency;
export const getCatalogResponsibleImAgency: GetCatalogResponsibleImAgency =
  <GetCatalogResponsibleImAgency>safeProp('responsibleImAgency');
